import React, { useEffect, useState, useRef, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldCheckIcon, TargetIcon, TrendingUpIcon, ZapIcon, UsersIcon, BarChart3Icon, GlobeIcon, StarIcon, ClockIcon, DollarSignIcon, AlertTriangleIcon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { NavigationHeaderEN } from '../components/NavigationHeaderEN';
import { ProofCarousel } from '../components/ProofCarousel';
import { EcgBackground } from '../components/EcgBackground';
import emailjs from '@emailjs/browser';
// Initialize EmailJS with public key
emailjs.init('vWvHy5XmFe6E1h_h6');
// ── Meta Pixel (Facebook) ──
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
;
(function () {
  if (typeof window !== 'undefined' && !window.fbq) {
    const n: any = window.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    s.parentNode?.insertBefore(t, s);
  }
})();
window.fbq('init', '1350274987135662');
function RejectIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-993">
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.5" data-id="element-994" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-995" />
    </svg>;
}
const EMAILJS_SERVICE_ID = 'service_5st6tfj';
const EMAILJS_TEMPLATE_ADMIN = 'template_kwi4ynr';
const EMAILJS_TEMPLATE_AUTOREPLY = 'template_2deikke';
const EMAILJS_PUBLIC_KEY = 'vWvHy5XmFe6E1h_h6';
const OFFICE_IMAGE = 'https://cdn.magicpatterns.com/uploads/i3vtP4KwJtKvGunCwNHaR1/A3ACF160-490D-4B87-A543-339A13041014.png';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
function CTAButton({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Meta Pixel: Track Lead event (user clicked CTA)
    if (window.fbq) window.fbq('track', 'Lead');
    const formSection = document.getElementById('formulario');
    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <motion.a href="#formulario" onClick={handleClick} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`
        inline-flex items-center justify-center gap-2 gold-gradient text-black font-bold rounded-full
        border border-transparent emerald-glow-hover transition-all duration-300
        px-6 py-4 text-sm sm:px-10 sm:py-5 sm:text-lg md:text-xl
        max-w-full whitespace-nowrap text-center cursor-pointer
        ${className}
      `} data-id="element-996">
      {children}
      <ArrowRightIcon className="w-5 h-5 flex-shrink-0" data-id="element-997" />
    </motion.a>;
}
function CTABlock() {
  return <div className="flex flex-col items-center gap-3 w-full" data-id="element-998">
      <CTAButton data-id="element-999">I WANT MY FREE DIAGNOSIS</CTAButton>
      <a href="https://calendar.app.google/PYPdcRoYne7QrNJA7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#D4A843]/70 hover:text-[#D4A843] text-sm transition-colors" data-id="element-1000">
        <CalendarIcon className="w-3.5 h-3.5" data-id="element-1001" />
        <span data-id="element-1002">Book an appointment for my free diagnosis</span>
      </a>
    </div>;
}
function SectionWrapper({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={fadeInUp} className={`relative py-14 md:py-18 px-4 md:px-8 overflow-hidden ${className}`} data-id="element-1003">
      <div className="relative z-10 max-w-6xl mx-auto" data-id="element-1004">{children}</div>
    </motion.section>;
}
function SectionDivider() {
  return <div className="w-full h-px bg-gray-800/50" data-id="element-1005" />;
}
function BackgroundSection({
  children,
  className = '',
  id = '',
  overlay = 'bg-[#0A0A0A]/90'
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  overlay?: string;
}) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={fadeInUp} className={`relative py-14 md:py-18 px-4 md:px-8 overflow-hidden ${className}`} style={{
    backgroundColor: '#0A0A0A'
  }} data-id="element-1006">
      <div className="relative z-10 max-w-6xl mx-auto" data-id="element-1007">{children}</div>
    </motion.section>;
}
function HeroSection() {
  const stats = [{
    value: '1,877+',
    label: 'Businesses scaled in the US',
    icon: TrendingUpIcon
  }, {
    value: '30 days',
    label: 'Results guarantee',
    icon: ShieldCheckIcon
  }, {
    value: 'Bilingual',
    label: 'We speak English & Spanish',
    icon: GlobeIcon
  }, {
    value: 'Tampa, FL',
    label: 'Physical offices',
    icon: MapPinIcon
  }];
  return <>
      <section className="relative overflow-hidden bg-[#0A0A0A]" data-id="element-1008">
        <EcgBackground intensity="subtle" position="bottom" speed={10} data-id="element-1009" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-36 md:pt-44 pb-10 md:pb-14" data-id="element-1010">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-1011">
            <motion.div variants={staggerItem} className="mb-6" data-id="element-1012">
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#10B981]/30 bg-[#059669]/10 text-[#10B981] text-xs font-semibold tracking-widest uppercase shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-1013">
                <ZapIcon className="w-3.5 h-3.5" data-id="element-1014" />
                Local Businesses in the USA
              </span>
            </motion.div>

            <motion.h1 variants={staggerItem} className="text-center text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4rem] font-black leading-[1.1] tracking-tight text-white max-w-4xl mb-8" data-id="element-1015">
              If you've already paid for ads, hired a "community manager" or
              followed a guru... and still{' '}
              <span className="gold-text" data-id="element-1016">
                CANNOT get more daily, consistent clients
              </span>
              , this is for you.
            </motion.h1>

            <motion.div variants={staggerItem} className="w-full max-w-4xl mx-auto mb-8" data-id="element-1017">
              <div className="relative" data-id="element-1018">
                <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-[#D4A843]/20 via-[#D4A843]/5 to-transparent" data-id="element-1019" />
                <img src={OFFICE_IMAGE} alt="Doctor Ecommerce Office — Tampa, FL" className="relative w-full rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]" data-id="element-1020" />
              </div>
            </motion.div>

            <motion.div variants={staggerItem} className="max-w-3xl mx-auto mb-8" data-id="element-1021">
              <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed mb-4" style={{
              textAlign: 'justify'
            }} data-id="element-1022">
                You don't lack the drive. You lack a{' '}
                <span className="text-white font-semibold" data-id="element-1023">
                  system that makes the right people find you, call you, and
                  hire you EVERY SINGLE DAY.
                </span>
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-400 leading-relaxed" style={{
              textAlign: 'justify'
            }} data-id="element-1024">
                We don't sell "branding", "pretty content" or hacks.{' '}
                <span className="text-white font-semibold" data-id="element-1025">
                  We build you a proprietary marketing system that generates
                  calls, appointments, and sales for your business.
                </span>{' '}
                We don't resell leads or share contacts. Every client that
                arrives, arrives because they saw YOUR business, YOUR offer, and
                wants to hire YOU.
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="mb-6" data-id="element-1026">
              <CTABlock data-id="element-1027" />
            </motion.div>

            <motion.div variants={staggerItem} className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-gray-500 text-xs md:text-sm" data-id="element-1028">
              <span className="flex items-center gap-1.5" data-id="element-1029">
                <GlobeIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-1030" /> Clients
                all over the United States
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1031">
                <MapPinIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-1032" />{' '}
                Physical offices in Tampa, FL
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1033">
                <ClockIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-1034" /> Response
                in under 2h
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-y border-[#D4A843]/20 bg-[#0A0A0A]" data-id="element-1035">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8" data-id="element-1036">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-1037">
            {stats.map((stat, index) => <motion.div key={index} variants={staggerItem} className="text-center relative" data-id="element-1038">
                {index > 0 && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-px bg-gradient-to-b from-transparent via-[#D4A843]/20 to-transparent hidden md:block" data-id="element-1039" />}
                <div className="flex justify-center mb-3" data-id="element-1040">
                  <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center" data-id="element-1041">
                    <stat.icon className="w-5 h-5 text-[#D4A843]" data-id="element-1042" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-1043">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-gray-400 mt-1 font-medium" data-id="element-1044">
                  {stat.label}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      <section className="bg-[#0A0A0A] py-16 md:py-24 px-4 sm:px-6" data-id="element-1045">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="max-w-4xl mx-auto" data-id="element-1046">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1047">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase mb-6" data-id="element-1048">
              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-1049" />
              Diagnosis
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 max-w-3xl mx-auto leading-tight" data-id="element-1050">
            This is happening to your business{' '}
            <span className="gold-text" data-id="element-1051">
              and every day that passes costs you money
            </span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-10" data-id="element-1052">
            It's not bad luck. It's a system problem — and it has a solution.
          </motion.p>

          <div className="space-y-4 md:space-y-5 mb-12" data-id="element-1053">
            {[{
            icon: BarChart3Icon,
            text: 'Your ads "get likes" but',
            highlight: 'do not generate calls',
            detail: 'Interaction without conversion is just entertainment. It is not marketing.'
          }, {
            icon: ShieldCheckIcon,
            text: 'You have "tried everything" and feel like',
            highlight: 'you were scammed',
            detail: 'Agencies, freelancers, courses... and the result was always the same: nothing.'
          }, {
            icon: TrendingUpIcon,
            text: 'Your competition is not better, but',
            highlight: 'they are taking your clients',
            detail: 'The best service does not win. The one with the best acquisition system wins.'
          }, {
            icon: ClockIcon,
            text: 'You are tired of hearing "give it time" while',
            highlight: 'the bills keep coming',
            detail: 'Patience does not pay the rent. You need measurable results now.'
          }, {
            icon: DollarSignIcon,
            text: 'They tell you "wait 3 months" and every month you pay the invoice of a guru who',
            highlight: 'sells more hope than clients',
            detail: 'If after 90 days there is no ROI, the problem is not you.'
          }, {
            icon: UsersIcon,
            text: 'You trade services for clients, and influencers',
            highlight: 'do not generate bookings or calls',
            detail: 'Traffic and interaction are not sales. Without appointments, there is no business.'
          }].map((point, index) => <motion.div key={index} variants={staggerItem} className="group relative bg-[#070707] border border-gray-800 rounded-xl overflow-hidden transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]" data-id="element-1054">
                <div className="flex items-start gap-4 md:gap-5 p-5 md:p-6" data-id="element-1055">
                  <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center mt-0.5" data-id="element-1056">
                    <point.icon className="w-5 h-5 text-[#D4A843]" data-id="element-1057" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1058">
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-1059">
                      {point.text}{' '}
                      <span className="text-white font-bold" data-id="element-1060">
                        {point.highlight}.
                      </span>
                    </p>
                    <p className="text-gray-500 text-xs md:text-sm mt-2 leading-relaxed" data-id="element-1061">
                      {point.detail}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-[#D4A843]/20 font-black text-2xl md:text-3xl leading-none select-none" data-id="element-1062">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </motion.div>)}
          </div>

          <motion.div variants={staggerItem} className="relative max-w-lg mx-auto mb-12" data-id="element-1063">
            <div className="relative bg-[#070707] border border-gray-800 rounded-xl p-6 md:p-8 text-center" data-id="element-1064">
              <div className="flex justify-center mb-4" data-id="element-1065">
                <div className="w-12 h-12 rounded-full gold-gradient flex items-center justify-center" data-id="element-1066">
                  <TargetIcon className="w-6 h-6 text-black" data-id="element-1067" />
                </div>
              </div>
              <p className="text-white font-bold text-lg md:text-xl mb-2" data-id="element-1068">
                Client Acquisition System
              </p>
              <p className="gold-text font-semibold text-sm md:text-base mb-1" data-id="element-1069">
                For local businesses in the USA — 100% in English
              </p>
              <p className="text-gray-500 text-sm" data-id="element-1070">
                Focused on the only thing that matters: calls, appointments, and
                sales
              </p>
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center" data-id="element-1071">
            <div className="flex justify-center mb-5" data-id="element-1072">
              <CTABlock data-id="element-1073" />
            </div>
            <p className="text-gray-600 text-xs md:text-sm italic max-w-md mx-auto leading-relaxed" data-id="element-1074">
              No commitment. We tell you the truth, what you need to implement
              and execute step by step, even if you don't work with us.
            </p>
          </motion.div>
        </motion.div>
      </section>
    </>;
}
function ProblemSection() {
  const problems = [{
    title: 'Your ads do not work',
    description: 'Not everyone who knows how to open Meta Ads Manager is an expert. Anyone can turn on an ad. The hard part is making that ad bring clients. If you have been paying for advertising for weeks or months and the results are not seen in your pocket, the answer is simple: the ads do not work and whoever makes them does not know what they are doing.',
    icon: BarChart3Icon
  }, {
    title: 'Your creatives are not targeted to the right audience',
    description: 'If the ad does not speak to the right person, Meta charges you more to show it to more people until finding someone who reacts. And if no one reacts, the cost goes up. And it keeps going up. And you keep paying. Without real metrics, there is no real growth.',
    icon: TargetIcon
  }, {
    title: 'You are the business owner — you already have enough on your plate',
    description: 'Trying to manage your own advertising on top of everything else has only one ending: exhaustion wins, results do not arrive, and you end up giving up. The ecosystem behind an advertising campaign that actually converts is much larger and more complex than anyone explained to you.',
    icon: UsersIcon
  }, {
    title: 'Your landing page says a lot... but communicates nothing',
    description: 'Your website does not generate authority. It looks generic. It feels like any other. And when someone lands there and sees you the same as everyone else, the question they ask themselves is inevitable: why should I hire you?',
    icon: GlobeIcon
  }, {
    title: 'There is no operational structure behind the business',
    description: 'There are no defined processes. There are no clear roles. There are no systems. Everything depends on the owner doing everything at the same time. So much effort, so much money invested in getting a lead... and when one finally arrives, the business is not ready to give the best version of itself.',
    icon: ZapIcon
  }, {
    title: 'Zero real measurement — every mistake is paid in cash',
    description: 'CTR. CPM. ROAS. CPC. They sound technical. But if your business is not scaling, the truth is different: not only do they not understand those metrics, they do not know what they are doing. They are gambling with your money. Every wrong decision is measured in dollars that left your pocket and did not return.',
    icon: DollarSignIcon
  }];
  const blameExcuses = ['Your niche is saturated', 'Your area is difficult', 'You need more budget', 'Your offer is not ready', 'You just have to make better content...'];
  return <>
      <SectionDivider data-id="element-1075" />
      <SectionWrapper className="bg-[#111]" data-id="element-1076">
        <EcgBackground intensity="subtle" position="bottom" speed={11} bgColor="#111" data-id="element-1077" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1078">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1079">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1080">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1081" />
              Analysis
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 max-w-4xl mx-auto leading-tight" data-id="element-1082">
            The reason why{' '}
            <span className="gold-text" data-id="element-1083">it didn't work for you before</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-lg md:text-xl text-gray-400 text-center mb-2" data-id="element-1084">
            (and it's not because your business is bad)
          </motion.p>
          <motion.p variants={staggerItem} className="text-base md:text-lg text-gray-500 text-center mb-16 md:mb-20" data-id="element-1085">
            Nor because you need more capital or a new branding
          </motion.p>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto mb-12" data-id="element-1086">
            <div className="relative" data-id="element-1087">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#D4A843] via-[#D4A843]/40 to-transparent" data-id="element-1088" />
              <div className="pl-8 md:pl-10" data-id="element-1089">
                <p className="text-lg md:text-xl text-gray-300 mb-6 leading-relaxed" style={{
                textAlign: 'justify'
              }} data-id="element-1090">
                  I'm going to tell you something that hurts, but sets you free:
                </p>
                <p className="text-lg md:text-xl text-white mb-8 leading-relaxed" style={{
                textAlign: 'justify'
              }} data-id="element-1091">
                  <span className="gold-text font-semibold" data-id="element-1092">
                    Most "agencies" and "gurus" do not fail due to bad
                    intentions. They fail because their model is designed to
                    charge you... not for you to win.
                  </span>
                </p>
                <p className="text-gray-400 mb-5 text-sm uppercase tracking-widest" data-id="element-1093">
                  And when it doesn't work, they blame you:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6" data-id="element-1094">
                  {blameExcuses.map((excuse, index) => <div key={index} className="flex items-center gap-3" data-id="element-1095">
                      <RejectIcon className="w-4 h-4 text-red-500/70 flex-shrink-0" data-id="element-1096" />
                      <span className="text-gray-400 text-sm md:text-base" data-id="element-1097">
                        "{excuse}"
                      </span>
                    </div>)}
                </div>
                <p className="text-gray-500 italic text-sm" data-id="element-1098">
                  Translation: pay me another month and hopefully something will
                  happen.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="max-w-4xl mx-auto mb-12" data-id="element-1099">
            <div className="space-y-0" data-id="element-1100">
              {problems.map((problem, index) => <motion.div key={index} variants={staggerItem} className="group" data-id="element-1101">
                  {index === 0 && <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-1102" />}
                  <div className="py-8 md:py-10" data-id="element-1103">
                    <div className="flex gap-6 md:gap-8" data-id="element-1104">
                      <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-1105">
                        <span className="text-[#D4A843]/15 font-black text-4xl md:text-5xl leading-none select-none" data-id="element-1106">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="w-9 h-9 rounded-lg bg-[#D4A843]/8 border border-[#D4A843]/15 flex items-center justify-center" data-id="element-1107">
                          <problem.icon className="w-4 h-4 text-[#D4A843]/60" data-id="element-1108" />
                        </div>
                      </div>
                      <div className="flex-1 pt-1" data-id="element-1109">
                        <h3 className="text-xl md:text-2xl font-bold mb-3 text-white leading-tight group-hover:text-[#D4A843] transition-colors duration-300" data-id="element-1110">
                          {problem.title}
                        </h3>
                        <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl" style={{
                      textAlign: 'justify'
                    }} data-id="element-1111">
                          {problem.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-1112" />
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto text-center" data-id="element-1113">
            <div className="flex items-center justify-center gap-4 mb-12" data-id="element-1114">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/60" data-id="element-1115" />
              <div className="w-2 h-2 rotate-45 border border-[#D4A843]/60" data-id="element-1116" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/60" data-id="element-1117" />
            </div>
            <p className="text-2xl md:text-3xl lg:text-4xl font-bold mb-10 leading-tight text-center" data-id="element-1118">
              And in the end, there is only one truth{' '}
              <span className="gold-text" data-id="element-1119">that matters</span>
            </p>
            <div className="relative mb-10" data-id="element-1120">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[#D4A843] opacity-[0.04] blur-[80px] rounded-full pointer-events-none" data-id="element-1121" />
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-[#D4A843]/40 via-[#D4A843]/15 to-[#D4A843]/5" data-id="element-1122" />
              <div className="relative bg-[#0A0A0A] rounded-2xl p-10 md:p-14" data-id="element-1123">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-[#D4A843]/[0.06] via-transparent to-transparent pointer-events-none" data-id="element-1124" />
                <div className="absolute top-6 left-8 md:top-8 md:left-10" data-id="element-1125">
                  <span className="text-[#D4A843]/10 text-6xl md:text-8xl font-serif leading-none select-none" data-id="element-1126">
                    "
                  </span>
                </div>
                <p className="relative text-lg md:text-xl lg:text-2xl text-gray-200 leading-relaxed text-center" style={{
                textAlign: 'justify'
              }} data-id="element-1127">
                  <span className="gold-text font-semibold" data-id="element-1128">
                    If your guru, your agency, or your "marketing expert" knew
                    what they were doing, you would already have a system
                    installed that makes clients come to your door every day,
                    wanting your products or services.
                  </span>
                </p>
                <div className="absolute bottom-4 right-8 md:bottom-6 md:right-10" data-id="element-1129">
                  <span className="text-[#D4A843]/10 text-6xl md:text-8xl font-serif leading-none select-none rotate-180 inline-block" data-id="element-1130">
                    "
                  </span>
                </div>
              </div>
            </div>
            <div className="text-center" data-id="element-1131">
              <p className="text-xl md:text-2xl lg:text-3xl font-black tracking-tight" data-id="element-1132">
                <span className="gold-text" data-id="element-1133">We fix all of that</span>{' '}
                <span className="text-white" data-id="element-1134">as a system.</span>
              </p>
              <div className="w-24 h-[2px] gold-gradient mx-auto mt-6 opacity-60" data-id="element-1135" />
            </div>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function SystemSection() {
  const phases = [{
    icon: TargetIcon,
    title: 'PHASE 1 — ATTRACTION',
    subtitle: 'We bring to your door the person who is already ready to buy.',
    description: 'We do not look for cold audiences who have never thought about your service. We use behavioral intelligence, intent data, and surgical segmentation to find exactly the person who today, right now, needs what you offer. Ready people. Not curious ones. Not followers. Buyers.'
  }, {
    icon: ZapIcon,
    title: 'PHASE 2 — CONVERSION',
    subtitle: 'We convert that interest into a call, an appointment, or a sale.',
    description: 'We build the complete bridge: a proposal designed specifically for your business, an experience that generates trust in seconds, an offer structured so that the client feels it would be a mistake not to act today, and the real differentiator of your business that you have had for years... but that no one has known how to communicate properly.'
  }, {
    icon: TrendingUpIcon,
    title: 'PHASE 3 — SCALE',
    subtitle: 'We build the system so that clients keep coming, predictable and in volume.',
    description: 'Once Phases 1 and 2 are working, the work becomes a system. Personalized communication keeps your business present in the mind of the right client, at the right time. Your business positions itself as the first option in your area. You do what you do best: your job.'
  }];
  const industries = ['Roofing', 'Landscaping', 'Moving', 'Handyman', 'Salons & Barber Shops', 'Spa & Aesthetics', 'Restaurants & Food Trucks', 'Cleaning', 'Accountants & Taxes', 'Lawyers', 'Doctors & Surgeons', 'Beauty Services', 'Pet Grooming', 'Bakeries & Catering', 'Home Remodeling', 'Auto Repair', 'Real Estate', 'Plumbing', 'HVAC', 'Electricians', 'Painting', 'Pest Control', 'Towing', 'Dentists', 'Fitness & Gym', 'Insurance', 'Flooring', 'Pressure Washing', 'Junk Removal', 'Car Wash & Detailing', 'Photography & Video', 'Events & Planning', 'Transportation / Trucking', 'Window Tinting', 'Locksmith', 'Daycare'];
  const differentiators = ['We do not depend on virality or followers', 'We do not sell "digital presence" — we install a system that makes clients come to your door', 'It is not a generic template — it is built on your specific business', 'It does not require you to be the expert — that is what we are here for', 'Every piece of the system works together — they are not loose parts', 'It is measured in results, not in reports'];
  return <>
      <SectionDivider data-id="element-1136" />
      <BackgroundSection overlay="bg-[#111]/95" data-id="element-1137">
        <EcgBackground intensity="medium" position="center" speed={8} bgColor="#111" data-id="element-1138" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1139">
          <motion.div variants={staggerItem} className="text-center mb-16" data-id="element-1140">
            <p className="text-[#D4A843] text-lg mb-4" data-id="element-1141">What do we really do?</p>
            <h2 className="text-3xl md:text-5xl mb-4" data-id="element-1142">
              It's not marketing. It's a{' '}
              <span className="gold-text" data-id="element-1143">
                System that makes clients come to your door. Every day.
              </span>
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1144">
              Designed and built specifically for your business.
            </p>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center mb-16" data-id="element-1145">
            <p className="text-2xl md:text-3xl font-bold mb-2" data-id="element-1146">Introducing:</p>
            <h3 className="text-4xl md:text-6xl gold-text text-shadow-gold font-black mb-4" data-id="element-1147">
              The A.C.E.™ System
            </h3>
            <p className="text-xl text-gray-400" data-id="element-1148">
              (Attraction. Conversion. Scale.)
            </p>
            <p className="text-lg text-gray-500 mt-4 max-w-3xl mx-auto italic" style={{
            textAlign: 'justify'
          }} data-id="element-1149">
              The first client acquisition system built specifically for local
              business owners in the United States.
            </p>
          </motion.div>

          <div className="space-y-6 mb-12" data-id="element-1150">
            {phases.map((phase, index) => {
            const PhaseIcon = phase.icon;
            return <motion.div key={index} variants={staggerItem} className="bg-[#070707] border border-gray-800 rounded-xl p-8 transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]" data-id="element-1151">
                  <div className="flex flex-col md:flex-row gap-6" data-id="element-1152">
                    <div className="flex-shrink-0" data-id="element-1153">
                      <div className="w-16 h-16 rounded-full gold-gradient flex items-center justify-center" data-id="element-1154">
                        <PhaseIcon className="w-8 h-8 text-black" data-id="element-1155" />
                      </div>
                    </div>
                    <div data-id="element-1156">
                      <h4 className="text-xl md:text-2xl font-bold gold-text mb-2" data-id="element-1157">
                        {phase.title}
                      </h4>
                      <p className="text-lg text-white mb-4" data-id="element-1158">
                        {phase.subtitle}
                      </p>
                      <p className="text-gray-400" style={{
                    textAlign: 'justify'
                  }} data-id="element-1159">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          <motion.div variants={staggerItem} className="mb-12" data-id="element-1160">
            <h4 className="text-2xl text-center mb-6" data-id="element-1161">
              The DOCTOR A.C.E.™ System has been implemented in:
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 max-w-4xl mx-auto" data-id="element-1162">
              {industries.map((industry, index) => industry === 'Roofing' ? <a key={index} href="https://roofing.thedoctorecommerce.com/" target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-sm text-gray-300 text-center cursor-pointer transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:text-white hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-1163">
                    {industry}
                  </a> : <span key={index} className="px-3 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-sm text-gray-300 text-center cursor-default transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:text-white hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-1164">
                    {industry}
                  </span>)}
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="mb-12" data-id="element-1165">
            <h4 className="text-2xl text-center mb-6" data-id="element-1166">
              What makes the A.C.E.™ System different:
            </h4>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-1167">
              {differentiators.map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1168">
                  <CheckIcon className="w-5 h-5 text-[#059669] flex-shrink-0" data-id="element-1169" />
                  <span className="text-gray-300" data-id="element-1170">{item}</span>
                </div>)}
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center" data-id="element-1171">
            <p className="text-2xl md:text-3xl font-bold mb-8" data-id="element-1172">
              One single goal. Every day.
            </p>
            <p className="text-xl text-gray-300 mb-8 italic" data-id="element-1173">
              "That when you open your business tomorrow... you already have
              clients knocking on your door, ready to hire you."
            </p>
            <CTABlock data-id="element-1174" />
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function IdentitySection() {
  return <>
      <SectionDivider data-id="element-1175" />
      <SectionWrapper className="bg-[#111]" data-id="element-1176">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1177">
          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-8" data-id="element-1178">
            You are not failing.{' '}
            <span className="gold-text" data-id="element-1179">
              They are selling you the wrong thing.
            </span>
          </motion.h2>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto" data-id="element-1180">
            <p className="text-xl text-gray-300 mb-6" style={{
            textAlign: 'justify'
          }} data-id="element-1181">
              <span className="text-white font-semibold" data-id="element-1182">
                You DO work hard. You DO open early. You DO answer the phone.
              </span>
            </p>
            <p className="text-lg text-gray-400 mb-8" style={{
            textAlign: 'justify'
          }} data-id="element-1183">
              But those who sell you marketing, often:
            </p>
            <ul className="text-left max-w-xl space-y-3 mb-8" data-id="element-1184">
              <li className="flex items-center gap-3 text-gray-400" data-id="element-1185">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-1186" />
                <span data-id="element-1187">Have never sold a real local service</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400" data-id="element-1188">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-1189" />
                <span data-id="element-1190">Do not understand your community</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400" data-id="element-1191">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-1192" />
                <span data-id="element-1193">
                  Do not understand that without clients there is no business
                </span>
              </li>
            </ul>
            <p className="text-2xl gold-text font-bold text-center" data-id="element-1194">
              We speak your language and we play this game to win.
            </p>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function ResultsSection() {
  const testimonials = [{
    name: 'Carlos M.',
    business: 'Roofing',
    location: 'Houston, TX',
    stars: 5,
    before: "I had been paying an agency $1,200/month for 4 months. They sent me pretty reports but the phone didn't ring. Two calls a week if I was lucky.",
    result: 'In the second week with Doctor Ecommerce I already had 8 daily calls. I had to hire a second crew.',
    highlight: 'From 2 calls/week → 8 calls/day'
  }, {
    name: 'María L.',
    business: 'Cleaning Services',
    location: 'Tampa, FL',
    stars: 5,
    before: 'I hired a "community manager" who charged me $800 a month. They only posted content on networks, but the phone never rang. Then I tried Facebook Ads myself and spent $600 without a single call.',
    result: 'Doctor Ecommerce set up the whole system for me. Now each lead costs me $12 and my schedule is full every week.',
    highlight: 'Cost per lead: from $45 → $12'
  }, {
    name: 'Ana R.',
    business: 'Beauty Salon',
    location: 'Los Angeles, CA',
    stars: 5,
    before: 'I traded services with influencers in exchange for "exposure". Lots of likes, zero appointments. I was about to close because I couldn\'t cover the rent for the place.',
    result: '3 weeks after implementing the system, my schedule was full for 3 weeks straight. Now I have a waiting list.',
    highlight: 'Schedule full for 3 consecutive weeks'
  }, {
    name: 'Jorge D.',
    business: 'Moving Company',
    location: 'Newark, NJ',
    stars: 5,
    before: 'A guru sold me a $2,000 course to "scale my business". I did everything he said — reels, content, hashtags. Result: 0 new moves in 2 months.',
    result: "With the A.C.E.™ System I got 47 bookings in 14 days. I had to turn down jobs because I couldn't keep up.",
    highlight: '47 bookings in just 14 days'
  }];
  return <>
      <SectionDivider data-id="element-1195" />
      <SectionWrapper id="casos" className="bg-[#0A0A0A]" data-id="element-1196">
        <EcgBackground intensity="subtle" position="center" speed={10} data-id="element-1197" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1198">
          <motion.div variants={staggerItem} className="text-center mb-4" data-id="element-1199">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1200">
              <StarIcon className="w-3.5 h-3.5" data-id="element-1201" />
              Real Cases
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4" data-id="element-1202">
            Real results. <span className="gold-text" data-id="element-1203">Real stories.</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-lg text-gray-400 text-center mb-14 max-w-2xl mx-auto" data-id="element-1204">
            These businesses were exactly where you are today. This is how they
            did after implementing the A.C.E.™ System
          </motion.p>

          <motion.div variants={staggerItem} className="mb-12" data-id="element-1205">
            <ProofCarousel data-id="element-1206" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-1207">
            {testimonials.map((t, index) => <motion.div key={index} variants={staggerItem} className="relative bg-[#070707] border border-gray-800 rounded-xl overflow-hidden" data-id="element-1208">
                <div className="p-6 md:p-8" data-id="element-1209">
                  <div className="flex items-center justify-between mb-5" data-id="element-1210">
                    <div className="flex items-center gap-1" data-id="element-1211">
                      {Array.from({
                    length: t.stars
                  }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#D4A843] fill-[#D4A843]" data-id="element-1212" />)}
                    </div>
                    <span className="text-xs text-gray-500 bg-[#1A1A1A] px-3 py-1 rounded-full border border-gray-800" data-id="element-1213">
                      {t.business}
                    </span>
                  </div>
                  <div className="relative mb-5" data-id="element-1214">
                    <span className="absolute -top-2 -left-1 text-[#D4A843]/15 text-4xl font-serif leading-none select-none" data-id="element-1215">
                      "
                    </span>
                    <p className="text-gray-400 text-sm leading-relaxed pl-5" style={{
                  textAlign: 'justify'
                }} data-id="element-1216">
                      {t.before}
                    </p>
                  </div>
                  <div className="bg-[#059669]/[0.06] border border-[#059669]/20 rounded-lg p-4 mb-5" data-id="element-1217">
                    <p className="text-gray-300 text-sm leading-relaxed mb-2" style={{
                  textAlign: 'justify'
                }} data-id="element-1218">
                      {t.result}
                    </p>
                    <div className="flex items-center gap-2" data-id="element-1219">
                      <TrendingUpIcon className="w-4 h-4 text-[#059669] flex-shrink-0" data-id="element-1220" />
                      <span className="text-[#059669] font-bold text-sm md:text-base" data-id="element-1221">
                        {t.highlight}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1222">
                    <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center flex-shrink-0" data-id="element-1223">
                      <span className="text-black font-bold text-sm" data-id="element-1224">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div data-id="element-1225">
                      <p className="text-white font-semibold text-sm" data-id="element-1226">
                        {t.name}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-1227">
                        {t.business} — {t.location}
                      </p>
                    </div>
                    <div className="ml-auto" data-id="element-1228">
                      <CheckIcon className="w-4 h-4 text-[#D4A843]/60" data-id="element-1229" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.p variants={staggerItem} className="text-sm text-gray-500 text-center mb-8 italic" data-id="element-1230">
            *Results vary based on area, offer, demand, and response capacity.
            We promise a real system and execution with real results.
          </motion.p>

          <motion.div variants={staggerItem} className="flex justify-center" data-id="element-1231">
            <CTAButton data-id="element-1232">I WANT RESULTS LIKE THIS</CTAButton>
          </motion.div>
          <div className="flex justify-center mt-3" data-id="element-1233">
            <a href="https://calendar.app.google/PYPdcRoYne7QrNJA7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#D4A843]/70 hover:text-[#D4A843] text-sm transition-colors" data-id="element-1234">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-1235" />
              <span data-id="element-1236">Book an appointment for my free diagnosis</span>
            </a>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function MediaSection() {
  const logos = [{
    src: 'https://cdn.magicpatterns.com/uploads/hzmUif78hbS4HfjFV6ucYy/IMG_7367.jpg',
    alt: 'Forbes'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/wykj9686A5gfVjv2YopME8/IMG_7395.webp',
    alt: 'Business Insider'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/gV6JpbvYezh1thFDnU3EGt/IMG_7396.webp',
    alt: 'Washington Post'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oMxvF2mwHZz5yfZmpCwY9k/IMG_0206.png',
    alt: 'USA Today'
  }];
  const duplicatedLogos = [...logos, ...logos];
  return <>
      <SectionDivider data-id="element-1237" />
      <SectionWrapper className="bg-[#111]" data-id="element-1238">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1239">
          <motion.h2 variants={staggerItem} className="text-2xl md:text-4xl text-center mb-4" data-id="element-1240">
            We are not "just another agency".{' '}
            <span className="gold-text" data-id="element-1241">We have real presence.</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-gray-400 text-center mb-12" data-id="element-1242">
            Featured in:
          </motion.p>

          <motion.div variants={staggerItem} className="relative" data-id="element-1243">
            <style data-id="element-1244">{`
              @keyframes marquee-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            `}</style>
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-[#111] to-transparent z-10 pointer-events-none" data-id="element-1245" />
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-[#111] to-transparent z-10 pointer-events-none" data-id="element-1246" />
            <div className="overflow-hidden" data-id="element-1247">
              <div className="flex items-center gap-12 md:gap-16 w-max" style={{
              animation: 'marquee-scroll 20s linear infinite'
            }} data-id="element-1248">
                {duplicatedLogos.map((logo, index) => <div key={index} className="flex-shrink-0 bg-white rounded-xl p-5 md:p-6 h-20 md:h-28 flex items-center justify-center" data-id="element-1249">
                    <img src={logo.src} alt={logo.alt} className="h-full w-auto object-contain max-w-[180px] md:max-w-[220px]" data-id="element-1250" />
                  </div>)}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function AntiGuruSection() {
  const guruLies = ['"Make 3 reels and clients will rain down on you"', '"Your energy attracts money"', '"Copy my millionaire funnel"', '"Automate everything and live on the beach"'];
  const realTools = ['Clear offer', 'Direct response ads', 'Landing page that converts', 'Aggressive (but professional) follow-up', 'Measurement and optimization'];
  return <>
      <SectionDivider data-id="element-1251" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-1252">
        <EcgBackground intensity="subtle" position="top" speed={9} data-id="element-1253" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1254">
          <motion.div variants={staggerItem} className="text-center mb-16 md:mb-20" data-id="element-1255">
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-gray-400 font-medium leading-snug mb-3" data-id="element-1256">
              If someone promised you wealth "overnight",
            </h2>
            <p className="text-3xl sm:text-4xl md:text-6xl font-black leading-[1.05] gold-text" data-id="element-1257">
              they sold you dopamine... not sales.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto" data-id="element-1258">
            <motion.div variants={staggerItem} className="mb-14 md:mb-16" data-id="element-1259">
              <p className="text-lg md:text-xl text-gray-300 mb-8" data-id="element-1260">
                Gurus sell you what feels good to hear:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1261">
                {guruLies.map((lie, index) => <motion.div key={index} variants={staggerItem} className="relative group" data-id="element-1262">
                    <div className="relative bg-[#070707] rounded-2xl p-5 md:p-6 border border-gray-800 overflow-hidden" data-id="element-1263">
                      <div className="relative flex items-center gap-4" data-id="element-1264">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-1265">
                          <RejectIcon className="w-4 h-4 text-red-400" data-id="element-1266" />
                        </div>
                        <span className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-1267">
                          {lie}
                        </span>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div variants={staggerItem} className="mb-16 md:mb-20" data-id="element-1268">
              <div className="relative rounded-3xl overflow-hidden" data-id="element-1269">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-[#D4A843] opacity-[0.04] blur-[100px] rounded-full pointer-events-none" data-id="element-1270" />

                <div className="relative bg-[#070707] rounded-3xl border border-gray-800 overflow-hidden" data-id="element-1271">
                  <div className="p-8 md:p-12 lg:p-14" data-id="element-1272">
                    <div className="mb-10" data-id="element-1273">
                      <p className="text-xl md:text-2xl text-white leading-relaxed" style={{
                      textAlign: 'justify'
                    }} data-id="element-1274">
                        <span className="gold-text font-bold" data-id="element-1275">
                          But you don't need motivation.
                        </span>{' '}
                        You need appointments. You need calls. You need sales
                        this week.
                      </p>
                    </div>

                    <div data-id="element-1276">
                      <p className="text-gray-400 text-sm uppercase tracking-[0.2em] font-semibold mb-6" data-id="element-1277">
                        And that is built with:
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1278">
                        {realTools.map((tool, index) => <div key={index} className="flex items-center gap-3 bg-[#0A0A0A] rounded-xl px-5 py-4 border border-gray-800 transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-1279">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#059669]/10 flex items-center justify-center" data-id="element-1280">
                              <CheckIcon className="w-3.5 h-3.5 text-[#059669]" data-id="element-1281" />
                            </div>
                            <span className="text-gray-200 text-sm font-medium" data-id="element-1282">
                              {tool}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={staggerItem} className="text-center" data-id="element-1283">
              <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight" data-id="element-1284">
                <span className="text-white" data-id="element-1285">
                  You don't come here to dream.
                </span>
                <br data-id="element-1286" />
                <span className="gold-text" data-id="element-1287">You come here to get paid.</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function ServicesSection() {
  const serviceCategories = [{
    icon: BarChart3Icon,
    title: 'CLIENT ACQUISITION — HIGH PERFORMANCE ADVERTISING',
    services: ['Ads designed to generate real clients, not likes or comments', 'We capture the person who is already looking for your service right now', "We chase whoever saw you but didn't act — until they act", 'Access to high-level advertising accounts with lower risk of restrictions']
  }, {
    icon: ShieldCheckIcon,
    title: 'DIGITAL SHIELDING — PROTECTION AND STABILITY',
    services: ['Anti-ban structure: professional configuration to reduce risk of suspensions', 'Optimized Ads profiles: Business Manager, Pixel, and domains configured correctly', 'Account backup and recovery: losing an account cannot mean losing your business']
  }, {
    icon: GlobeIcon,
    title: 'DIGITAL PRESENCE WITH AUTHORITY',
    services: ['High-impact websites: designed to generate trust and convert', 'Local SEO: so they find you when they search for your service in your city', 'Conversion landing pages: specific pages for each campaign']
  }, {
    icon: StarIcon,
    title: 'REPUTATION AND SOCIAL PROOF',
    services: ['Strategy to appear first with authority when they search for your service', 'System to get reviews from real clients consistently', 'Online reputation management: we control the narrative of your business', 'Building social proof: testimonials and success stories']
  }, {
    icon: ZapIcon,
    title: 'AUTOMATION AND FOLLOW-UP',
    services: ['Automated follow-up: WhatsApp, SMS, and email that respond without you doing anything', 'Reactivation of dormant leads: those who said "later" — we bring them back', 'Weekly campaign optimization: we review, adjust, and scale every week']
  }];
  const DiagnosisIcon = () => <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" data-id="element-1288">
      <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.5" data-id="element-1289" />
      <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-1290" />
      <circle cx="16" cy="16" r="2" fill="currentColor" data-id="element-1291" />
      <line x1="16" y1="1" x2="16" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-1292" />
      <line x1="16" y1="25" x2="16" y2="31" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-1293" />
      <line x1="1" y1="16" x2="7" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-1294" />
      <line x1="25" y1="16" x2="31" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-1295" />
    </svg>;
  return <>
      <SectionDivider data-id="element-1296" />
      <BackgroundSection overlay="bg-[#111]/95" data-id="element-1297">
        <EcgBackground intensity="subtle" position="bottom" speed={10} bgColor="#111" data-id="element-1298" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1299">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1300">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1301">
              <ZapIcon className="w-3.5 h-3.5" data-id="element-1302" />
              Complete Ecosystem
            </span>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1303">
            <p className="text-[#D4A843] text-base md:text-lg mb-4 tracking-wide" data-id="element-1304">
              What does working with us include?
            </p>
            <h2 className="text-3xl md:text-5xl mb-4 leading-tight" data-id="element-1305">
              We don't sell you a service.{' '}
              <span className="gold-text" data-id="element-1306">
                We integrate a complete ecosystem for you.
              </span>
            </h2>
          </motion.div>

          <motion.div variants={staggerItem} className="flex items-center justify-center gap-4 mb-16 md:mb-20" data-id="element-1307">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-1308" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[#D4A843]/30" data-id="element-1309" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-1310" />
          </motion.div>

          <motion.div variants={staggerItem} className="mb-16" data-id="element-1311">
            <div className="flex items-center gap-4 mb-8" data-id="element-1312">
              <div className="flex items-center gap-3" data-id="element-1313">
                <div className="w-10 h-10 rounded-full border-2 border-[#D4A843] flex items-center justify-center" data-id="element-1314">
                  <span className="text-[#D4A843] font-bold text-sm" data-id="element-1315">01</span>
                </div>
                <div className="h-px w-8 bg-gradient-to-r from-[#D4A843]/60 to-transparent" data-id="element-1316" />
              </div>
              <p className="text-sm text-gray-400 font-semibold tracking-[0.15em] uppercase" data-id="element-1317">
                Before charging a single dollar
              </p>
            </div>

            <div className="relative" data-id="element-1318">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.04] hover:shadow-[0_0_24px_rgba(5,150,105,0.15)] group/diag" data-id="element-1319">
                <div className="relative p-8 md:p-12" data-id="element-1320">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8" data-id="element-1321">
                    <div className="flex-shrink-0 w-16 h-16 rounded-xl gold-gradient flex items-center justify-center" data-id="element-1322">
                      <DiagnosisIcon data-id="element-1323" />
                    </div>
                    <div className="text-center md:text-left" data-id="element-1324">
                      <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 leading-tight" data-id="element-1325">
                        Client Acquisition Diagnosis
                      </h3>
                      <div className="flex items-center justify-center md:justify-start gap-3 mt-3" data-id="element-1326">
                        <div className="h-px w-8 bg-[#D4A843]/40" data-id="element-1327" />
                        <p className="text-xl md:text-2xl gold-text font-black tracking-wide" data-id="element-1328">
                          COMPLETELY FREE
                        </p>
                        <div className="h-px w-8 bg-[#D4A843]/40" data-id="element-1329" />
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-8" data-id="element-1330" />

                  <div className="max-w-3xl" data-id="element-1331">
                    <p className="text-gray-300 italic mb-8 text-base leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-1332">
                      This is not a sales call disguised as consulting.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4 mb-8" data-id="element-1333">
                      {['Why clients are not coming to you and what we will do to change it', 'What is slowing down the growth of your business specifically', 'What is the biggest opportunity you have right now', 'Step-by-step plan to see changes in the next 7 days'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-[#0A0A0A] rounded-lg p-4 border border-gray-800 transition-all duration-300 group-hover/diag:border-[#10B981]/40 group-hover/diag:bg-[#059669]/[0.06] group-hover/diag:shadow-[0_0_12px_rgba(5,150,105,0.15)]" data-id="element-1334">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#059669]/10 border border-[#059669]/30 flex items-center justify-center mt-0.5" data-id="element-1335">
                            <CheckIcon className="w-3.5 h-3.5 text-[#059669]" data-id="element-1336" />
                          </div>
                          <span className="text-gray-300 text-sm leading-relaxed" data-id="element-1337">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <p className="text-gray-400 italic text-sm leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-1338">
                      And we tell you everything. Even if you don't work with
                      us.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="mb-16" data-id="element-1339">
            <div className="flex items-center gap-4 mb-10" data-id="element-1340">
              <div className="flex items-center gap-3" data-id="element-1341">
                <div className="w-10 h-10 rounded-full border-2 border-[#D4A843] flex items-center justify-center" data-id="element-1342">
                  <span className="text-[#D4A843] font-bold text-sm" data-id="element-1343">02</span>
                </div>
                <div className="h-px w-8 bg-gradient-to-r from-[#D4A843]/60 to-transparent" data-id="element-1344" />
              </div>
              <p className="text-sm text-gray-400 font-semibold tracking-[0.15em] uppercase" data-id="element-1345">
                If you decide to move forward
              </p>
            </div>

            <div className="space-y-0" data-id="element-1346">
              {serviceCategories.map((category, index) => {
              const CategoryIcon = category.icon;
              return <motion.div key={index} variants={staggerItem} className="group" data-id="element-1347">
                    {index === 0 && <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" data-id="element-1348" />}
                    <div className="py-8 md:py-10" data-id="element-1349">
                      <div className="flex gap-5 md:gap-8" data-id="element-1350">
                        <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-1351">
                          <span className="text-[#D4A843]/15 font-black text-3xl md:text-4xl leading-none select-none" data-id="element-1352">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="w-11 h-11 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center group-hover:bg-[#D4A843]/15 group-hover:border-[#D4A843]/40 transition-all duration-300" data-id="element-1353">
                            <CategoryIcon className="w-5 h-5 text-[#D4A843]" data-id="element-1354" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1355">
                          <h4 className="text-base md:text-lg font-bold text-white mb-4 leading-tight group-hover:text-[#D4A843] transition-colors duration-300" data-id="element-1356">
                            {category.title}
                          </h4>
                          <ul className="space-y-2.5" data-id="element-1357">
                            {category.services.map((service, sIndex) => <li key={sIndex} className="flex items-start gap-3 text-gray-400 text-sm" data-id="element-1358">
                                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#059669] mt-2" data-id="element-1359" />
                                <span className="leading-relaxed" data-id="element-1360">
                                  {service}
                                </span>
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" data-id="element-1361" />
                  </motion.div>;
            })}
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center" data-id="element-1362">
            <div className="flex items-center justify-center gap-4 mb-10" data-id="element-1363">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-1364" />
              <div className="w-2 h-2 rotate-45 border border-[#D4A843]/40" data-id="element-1365" />
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-1366" />
            </div>
            <p className="text-xl text-gray-300 mb-8 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-1367">
              While you do your job —{' '}
              <span className="gold-text font-semibold" data-id="element-1368">
                we build the system that makes clients find you and call you.
              </span>
            </p>
            <div className="flex justify-center" data-id="element-1369">
              <CTABlock data-id="element-1370" />
            </div>
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function QualificationSection() {
  const forYou = ['Business owners who can handle more clients', 'Who are ready to follow a process', 'Who want immediate results, not "branding"'];
  const notForYou = ['If you want to "go viral" without selling anything', 'If you do not answer calls or messages', 'If you are looking for magic with $1/day', 'If you want marketing to work but without changing anything'];
  const ApproveShieldIcon = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-1371">
      <path d="M14 2L4 7v6.5c0 6.5 4.3 12.6 10 14 5.7-1.4 10-7.5 10-14V7L14 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" data-id="element-1372" />
      <path d="M9.5 14.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1373" />
    </svg>;
  const DenyShieldIcon = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-1374">
      <path d="M14 2L4 7v6.5c0 6.5 4.3 12.6 10 14 5.7-1.4 10-7.5 10-14V7L14 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" data-id="element-1375" />
      <path d="M10.5 10.5l7 7M17.5 10.5l-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-1376" />
    </svg>;
  const BulletCheck = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-1377">
      <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.3" data-id="element-1378" />
      <path d="M5.5 9.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1379" />
    </svg>;
  const BulletX = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-1380">
      <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.3" data-id="element-1381" />
      <path d="M6.5 6.5l5 5M11.5 6.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-1382" />
    </svg>;
  return <>
      <SectionDivider data-id="element-1383" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-1384">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1385">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1386">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1387">
              <TargetIcon className="w-3.5 h-3.5" data-id="element-1388" />
              Qualification
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-1389">
            Is this <span className="gold-text" data-id="element-1390">for you</span>?
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-xl mx-auto" data-id="element-1391">
            We don't work with everyone. We work with those who are ready to
            grow.
          </motion.p>

          <motion.div variants={staggerItem} className="flex items-center justify-center gap-4 mb-14" data-id="element-1392">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-1393" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[#D4A843]/30" data-id="element-1394" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-1395" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto" data-id="element-1396">
            <motion.div variants={staggerItem} className="relative group" data-id="element-1397">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl p-8 md:p-10 overflow-hidden" data-id="element-1398">
                <div className="relative flex items-center gap-4 mb-8" data-id="element-1399">
                  <div className="w-12 h-12 rounded-xl bg-[#D4A843]/10 border border-[#D4A843]/25 flex items-center justify-center" data-id="element-1400">
                    <ApproveShieldIcon className="w-7 h-7 text-[#D4A843]" data-id="element-1401" />
                  </div>
                  <div data-id="element-1402">
                    <p className="text-xs text-[#D4A843]/60 font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-1403">
                      Ideal for
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-tight" data-id="element-1404">
                      Who it IS for
                    </h3>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-[#D4A843]/20 via-[#D4A843]/10 to-transparent mb-7" data-id="element-1405" />
                <ul className="relative space-y-5" data-id="element-1406">
                  {forYou.map((item, index) => <li key={index} className="flex items-start gap-3.5" data-id="element-1407">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-1408">
                        <BulletCheck className="w-5 h-5 text-[#D4A843]" data-id="element-1409" />
                      </div>
                      <span className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-1410">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>

            <motion.div variants={staggerItem} className="relative group" data-id="element-1411">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl p-8 md:p-10 overflow-hidden" data-id="element-1412">
                <div className="relative flex items-center gap-4 mb-8" data-id="element-1413">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-gray-700/50 flex items-center justify-center" data-id="element-1414">
                    <DenyShieldIcon className="w-7 h-7 text-gray-500" data-id="element-1415" />
                  </div>
                  <div data-id="element-1416">
                    <p className="text-xs text-gray-600 font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-1417">
                      Not for
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-tight" data-id="element-1418">
                      Who it is NOT for
                    </h3>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-gray-700/30 via-gray-800/20 to-transparent mb-7" data-id="element-1419" />
                <ul className="relative space-y-5" data-id="element-1420">
                  {notForYou.map((item, index) => <li key={index} className="flex items-start gap-3.5" data-id="element-1421">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-1422">
                        <BulletX className="w-5 h-5 text-gray-600" data-id="element-1423" />
                      </div>
                      <span className="text-gray-500 text-sm md:text-base leading-relaxed" data-id="element-1424">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function GuaranteeSection() {
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <SectionDivider data-id="element-1425" />
      <BackgroundSection data-id="element-1426">
        <EcgBackground intensity="subtle" position="top" speed={12} data-id="element-1427" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1428">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1429">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1430">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1431" />
              Guarantee
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-1432">
            We put our money{' '}
            <span className="gold-text" data-id="element-1433">where our mouth is.</span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-xl mx-auto" data-id="element-1434">
            We don't ask you to trust blindly. We give you a real guarantee.
          </motion.p>

          <motion.div variants={staggerItem} className="max-w-4xl mx-auto" data-id="element-1435">
            <div className="relative rounded-3xl overflow-hidden" data-id="element-1436">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] bg-[#D4A843] opacity-[0.04] blur-[100px] rounded-full pointer-events-none" data-id="element-1437" />
              <div className="relative bg-[#111] rounded-3xl border border-gray-800/60 overflow-hidden" data-id="element-1438">
                <div className="h-1 gold-gradient" data-id="element-1439" />
                <div className="p-8 md:p-12 lg:p-14" data-id="element-1440">
                  <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-10" data-id="element-1441">
                    <div className="flex-shrink-0 text-center md:text-left" data-id="element-1442">
                      <div className="relative inline-block" data-id="element-1443">
                        <span className="text-7xl md:text-8xl lg:text-9xl font-black gold-text leading-none tracking-tighter" data-id="element-1444">
                          30
                        </span>
                        <span className="block text-lg md:text-xl font-bold text-white -mt-1 tracking-wide" data-id="element-1445">
                          DAYS
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left" data-id="element-1446">
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight" data-id="element-1447">
                        THE A.C.E.™ GUARANTEE
                      </h3>
                      <p className="text-gray-300 text-base md:text-lg leading-relaxed" style={{
                      textAlign: 'justify'
                    }} data-id="element-1448">
                        If in the first 30 days of working together you don't
                        see a real, measurable increase in the number of leads
                        coming into your business every day, we will refund{' '}
                        <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#D4A843]/10 border border-[#D4A843]/25 text-[#D4A843] font-bold text-sm align-middle" data-id="element-1449">
                          100% of your investment
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="h-px bg-gray-800/80 mb-10" data-id="element-1450" />

                  <div className="mb-10" data-id="element-1451">
                    <p className="text-sm text-gray-500 font-semibold tracking-[0.15em] uppercase mb-6" data-id="element-1452">
                      What does this guarantee cover?
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4" data-id="element-1453">
                      {[{
                      stat: 'Leads',
                      desc: 'Measurable and demonstrable increase in the volume of incoming leads'
                    }, {
                      stat: 'Qualified',
                      desc: 'Leads within the agreed niche and geographical area'
                    }, {
                      stat: '7 days',
                      desc: 'Complete implementation of the system before the end of the first week'
                    }].map((item, i) => <div key={i} className="bg-[#0A0A0A] rounded-2xl p-5 md:p-6" data-id="element-1454">
                          <p className="text-[#D4A843] font-black text-xl md:text-2xl mb-2" data-id="element-1455">
                            {item.stat}
                          </p>
                          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1456">
                            {item.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>

                  <div className="bg-[#0A0A0A] rounded-2xl p-6 md:p-8 mb-8" data-id="element-1457">
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-1458">
                      <span className="font-bold text-white" data-id="element-1459">
                        As simple as that. As direct as that.
                      </span>{' '}
                      Because we don't live off charging you for months without
                      results. We live off your business growing — and you
                      telling others about it.
                    </p>
                  </div>

                  <div data-id="element-1460">
                    <button onClick={() => setShowTerms(!showTerms)} className="flex items-center gap-3 text-gray-500 hover:text-gray-300 transition-colors group" data-id="element-1461">
                      <motion.div animate={{
                      rotate: showTerms ? 180 : 0
                    }} transition={{
                      duration: 0.3,
                      ease: [0.4, 0, 0.2, 1]
                    }} data-id="element-1462">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-1463" />
                      </motion.div>
                      <span className="text-sm font-medium" data-id="element-1464">
                        Terms and conditions of the guarantee
                      </span>
                    </button>

                    <motion.div initial={false} animate={{
                    height: showTerms ? 'auto' : 0,
                    opacity: showTerms ? 1 : 0
                  }} transition={{
                    height: {
                      duration: 0.4,
                      ease: [0.4, 0, 0.2, 1]
                    },
                    opacity: {
                      duration: showTerms ? 0.35 : 0.2,
                      delay: showTerms ? 0.1 : 0
                    }
                  }} className="overflow-hidden" data-id="element-1465">
                      <div className="pt-5 text-xs text-gray-500 space-y-2" data-id="element-1466">
                        <p data-id="element-1467">This guarantee applies as long as:</p>
                        <ul className="space-y-1.5 ml-4" data-id="element-1468">
                          {['The agreed advertising budget is available and active from the start', 'The client responds on time to requests for information, materials, or approvals', 'The business offer is real, legal, and deliverable', 'There has been no interference, unilateral changes, or cancellations of active campaigns', 'The 30-day period is counted from the official launch date of the campaigns', 'The client has attended to the leads received'].map((term, i) => <li key={i} className="flex items-start gap-2" data-id="element-1469">
                              <span className="text-gray-600 mt-0.5" data-id="element-1470">•</span>
                              <span data-id="element-1471">{term}</span>
                            </li>)}
                        </ul>
                        <p className="italic text-gray-600 pt-1" data-id="element-1472">
                          This guarantee does not apply if the client does not
                          comply with the operational agreements established at
                          the beginning of the project.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center mt-12" data-id="element-1473">
            <p className="text-lg md:text-xl text-gray-300 mb-6" data-id="element-1474">
              No risk. No excuses. No smoke.
            </p>
            <div className="flex justify-center" data-id="element-1475">
              <CTABlock data-id="element-1476" />
            </div>
            <p className="text-gray-600 text-sm mt-4 italic" data-id="element-1477">
              If in 30 days you don't see more leads coming into your business,
              you don't pay.
            </p>
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: "I already ran ads and it didn't work.",
    answer: 'Perfect. That means you already paid for the lesson. Most fail for 3 reasons: offer, landing page, and follow-up. The ad alone does not save a business. We fix the entire system.'
  }, {
    question: 'I already had an agency and it went poorly.',
    answer: 'Normal. Many agencies charge to "manage", not to convert. Reports ≠ sales. Here we measure calls, appointments, and closings.'
  }, {
    question: 'Do I need a large budget?',
    answer: 'You need the right budget, not a "large" one. If your service leaves a good margin, as long as the cost per lead is profitable, it scales. If your margin is low, we adjust the offer and upsells.'
  }, {
    question: 'What if my city is saturated?',
    answer: 'Content is saturated, not the market. If there is competition, there is demand. Winning depends on offer + speed + trust + follow-up.'
  }, {
    question: "I don't have time.",
    answer: "If you don't have time to get clients today, tomorrow you will have plenty of time. This is done quickly and with structure."
  }];
  return <>
      <SectionDivider data-id="element-1478" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-1479">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1480">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1481">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1482">
              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-1483" />
              Doubts
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-1484">
            Frequently Asked <span className="gold-text" data-id="element-1485">Questions</span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-lg mx-auto" data-id="element-1486">
            If you still have doubts, the answer is probably here.
          </motion.p>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto space-y-3" data-id="element-1487">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <div key={index} className={`relative rounded-2xl transition-all duration-300 ${isOpen ? 'bg-[#070707] border border-[#10B981] bg-[#059669]/[0.06] shadow-[0_0_16px_rgba(5,150,105,0.2)]' : 'bg-[#0A0A0A] border border-gray-800 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]'}`} data-id="element-1488">
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#D4A843] to-[#D4A843]/40 rounded-l-2xl transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} data-id="element-1489" />
                  <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-6 md:px-8 py-6 text-left flex items-center gap-5 group cursor-pointer" data-id="element-1490">
                    <span className={`flex-shrink-0 text-sm font-bold tabular-nums transition-colors duration-300 ${isOpen ? 'text-[#D4A843]' : 'text-gray-600'}`} data-id="element-1491">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className={`flex-1 text-base md:text-lg font-semibold leading-snug transition-colors duration-300 ${isOpen ? 'text-white' : 'text-gray-300 group-hover:text-white'}`} data-id="element-1492">
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#D4A843]/15 border border-[#D4A843]/40' : 'bg-[#D4A843]/5 border border-[#D4A843]/20 group-hover:bg-[#D4A843]/10 group-hover:border-[#D4A843]/30'}`} data-id="element-1493">
                      <ChevronDownIcon className={`w-4 h-4 text-[#D4A843] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1494" />
                    </div>
                  </button>
                  {isOpen && <div className="px-6 md:px-8 pb-6 pl-[4.25rem] md:pl-[4.75rem] animate-[fadeIn_0.3s_ease-out]" data-id="element-1495">
                      <p className="text-gray-400 text-sm md:text-base leading-relaxed" data-id="element-1496">
                        {faq.answer}
                      </p>
                    </div>}
                </div>;
          })}
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function FormSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city_state: '',
    business_type: '',
    business_name: '',
    instagram: '',
    avg_ticket: '',
    clients_per_week: '',
    new_clients_needed: '',
    ads_before: '',
    ads_experience: '',
    monthly_ad_spend_range: ''
  });
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendStatus('sending');
    try {
      const form = formRef.current;
      if (!form) throw new Error('Form not found');
      // Fill hidden tracking fields
      const pageUrlInput = form.querySelector<HTMLInputElement>('input[name="page_url"]');
      const dateInput = form.querySelector<HTMLInputElement>('input[name="date"]');
      if (pageUrlInput) pageUrlInput.value = window.location.href;
      if (dateInput) dateInput.value = new Date().toLocaleString();
      // 1) Email para ADMIN (notificación con datos del lead)
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, form, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY para el CLIENTE (confirmación)
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, form, EMAILJS_PUBLIC_KEY);
      setSendStatus('sent');
      // Meta Pixel: Track CompleteRegistration (form submitted with all data)
      if (window.fbq) window.fbq('track', 'CompleteRegistration');
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        city_state: '',
        business_type: '',
        business_name: '',
        instagram: '',
        avg_ticket: '',
        clients_per_week: '',
        new_clients_needed: '',
        ads_before: '',
        ads_experience: '',
        monthly_ad_spend_range: ''
      });
      setTimeout(() => setSendStatus('idle'), 4000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSendStatus('error');
      setTimeout(() => setSendStatus('idle'), 4000);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <>
      <SectionDivider data-id="element-1497" />
      <BackgroundSection id="formulario" overlay="bg-[#111]/94" data-id="element-1498">
        <EcgBackground intensity="medium" position="bottom" speed={9} bgColor="#111" data-id="element-1499" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-1500">
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-1501">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1502">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-1503" />
              Free Diagnosis
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-center mb-3" data-id="element-1504">
            <span className="block text-lg md:text-xl text-gray-400 mb-3 tracking-wide" data-id="element-1505">
              If you are tired of trying and failing...
            </span>
            <span className="block text-4xl md:text-6xl font-black gold-text leading-[1.1]" data-id="element-1506">
              It ends today.
            </span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-base md:text-lg text-gray-400 text-center mb-14 max-w-2xl mx-auto leading-relaxed" data-id="element-1507">
            Book your diagnosis and we will tell you exactly what is blocking
            your clients, what to change this week, and what your funnel should
            look like to produce daily.
          </motion.p>

          <motion.form ref={formRef} id="lead-form" variants={staggerItem} onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-[#1A1A1A]/95 backdrop-blur-sm border border-[#D4A843]/30 rounded-xl p-8" data-id="element-1508">
            {/* Hidden tracking fields */}
            <input type="hidden" name="page_url" data-id="element-1509" />
            <input type="hidden" name="date" data-id="element-1510" />

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1511">
              <div data-id="element-1512">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1513">
                  Name *
                </label>
                <input type="text" name="full_name" required value={formData.full_name} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Your name" data-id="element-1514" />
              </div>
              <div data-id="element-1515">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1516">
                  Email *
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="you@email.com" data-id="element-1517" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1518">
              <div data-id="element-1519">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1520">
                  Phone *
                </label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="(123) 456-7890" data-id="element-1521" />
              </div>
              <div data-id="element-1522">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1523">
                  City/State *
                </label>
                <input type="text" name="city_state" required value={formData.city_state} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Miami, FL" data-id="element-1524" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1525">
              <div data-id="element-1526">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1527">
                  Business Type *
                </label>
                <input type="text" name="business_type" required value={formData.business_type} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Roofing, Salon, Cleaning..." data-id="element-1528" />
              </div>
              <div data-id="element-1529">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1530">
                  Business Name *
                </label>
                <input type="text" name="business_name" required value={formData.business_name} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="My Business LLC" data-id="element-1531" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1532">
              <div data-id="element-1533">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1534">
                  Business Instagram *
                </label>
                <input type="text" name="instagram" required value={formData.instagram} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="@yourbusiness" data-id="element-1535" />
              </div>
              <div data-id="element-1536">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1537">
                  Average ticket per sale ($)
                </label>
                <input type="text" name="avg_ticket" value={formData.avg_ticket} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="$500" data-id="element-1538" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1539">
              <div data-id="element-1540">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1541">
                  How many clients do you have per week?
                </label>
                <input type="text" name="clients_per_week" value={formData.clients_per_week} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="5-10" data-id="element-1542" />
              </div>
              <div data-id="element-1543">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1544">
                  How many new clients do you need per week? *
                </label>
                <input type="text" name="new_clients_needed" required value={formData.new_clients_needed} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="10-20" data-id="element-1545" />
              </div>
            </div>

            <div className="mb-6" data-id="element-1546">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1547">
                Have you invested in ads before? *
              </label>
              <div className="flex gap-4" data-id="element-1548">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1549">
                  <input type="radio" name="ads_before" value="Yes" checked={formData.ads_before === 'Yes'} onChange={handleChange} className="w-4 h-4 text-[#D4A843] bg-[#0A0A0A] border-gray-700 focus:ring-[#D4A843]" data-id="element-1550" />
                  <span className="text-gray-300" data-id="element-1551">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1552">
                  <input type="radio" name="ads_before" value="No" checked={formData.ads_before === 'No'} onChange={handleChange} className="w-4 h-4 text-[#D4A843] bg-[#0A0A0A] border-gray-700 focus:ring-[#D4A843]" data-id="element-1553" />
                  <span className="text-gray-300" data-id="element-1554">No</span>
                </label>
              </div>
            </div>

            {formData.ads_before === 'Yes' && <div className="mb-6" data-id="element-1555">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1556">
                  What happened?
                </label>
                <textarea name="ads_experience" value={formData.ads_experience} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors resize-none" placeholder="Briefly tell us about your experience..." data-id="element-1557" />
              </div>}

            <div className="mb-8" data-id="element-1558">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1559">
                How much money are you currently spending on advertising per
                month? *
              </label>
              <select name="monthly_ad_spend_range" required value={formData.monthly_ad_spend_range} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-1560">
                <option value="" data-id="element-1561">Select an option</option>
                <option value="0-500" data-id="element-1562">$0 - $500</option>
                <option value="500-1500" data-id="element-1563">$500 - $1,500</option>
                <option value="1500-5000" data-id="element-1564">$1,500 - $5,000</option>
                <option value="5000+" data-id="element-1565">More than $5,000</option>
              </select>
            </div>

            <div className="flex justify-center" data-id="element-1566">
              <motion.button type="submit" disabled={sendStatus === 'sending'} whileHover={sendStatus === 'idle' ? {
              scale: 1.02
            } : {}} whileTap={sendStatus === 'idle' ? {
              scale: 0.98
            } : {}} className={`font-bold py-2.5 px-8 rounded-md text-sm border border-transparent transition-all duration-300 text-center whitespace-nowrap ${sendStatus === 'sent' ? 'bg-[#059669] text-white' : sendStatus === 'error' ? 'bg-red-500 text-white' : sendStatus === 'sending' ? 'gold-gradient text-black opacity-70 cursor-wait' : 'gold-gradient text-black emerald-glow-hover'}`} data-id="element-1567">
                {sendStatus === 'sending' && 'SENDING...'}
                {sendStatus === 'sent' && '✓ REQUEST SENT SUCCESSFULLY!'}
                {sendStatus === 'error' && '✕ ERROR — TRY AGAIN'}
                {sendStatus === 'idle' && 'GET MY FREE DIAGNOSIS'}
              </motion.button>
            </div>

            <div className="text-center mt-6" data-id="element-1568">
              <div className="inline-flex items-center gap-3 bg-[#0A0A0A] border border-[#D4A843]/20 rounded-full px-6 py-2.5" data-id="element-1569">
                <span className="text-gray-500 line-through text-sm" data-id="element-1570">
                  Value: $500 USD
                </span>
                <span className="text-[#059669] font-bold text-sm" data-id="element-1571">
                  TODAY: FREE
                </span>
              </div>
            </div>
          </motion.form>
        </motion.div>
      </BackgroundSection>
    </>;
}
function Footer() {
  const [footerName, setFooterName] = useState('');
  const [footerPhone, setFooterPhone] = useState('');
  const [footerBusiness, setFooterBusiness] = useState('');
  const [footerStatus, setFooterStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [openResource, setOpenResource] = useState<number | null>(null);
  const handleFooterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!footerName || !footerPhone || !footerBusiness) return;
    setFooterStatus('sending');
    try {
      const footerParams = {
        nombre: footerName,
        email: '',
        negocio: footerBusiness,
        telefono: footerPhone,
        inversion: 'Not specified',
        mensaje: `👤 Name: ${footerName}\n📞 Phone: ${footerPhone}\n🏢 Business: ${footerBusiness}\n📍 Source: Footer Mini Form EN`,
        to_email: 'doctor.ecommerceus@gmail.com',
        page_url: window.location.href,
        date: new Date().toLocaleString()
      };
      // 1) Email para ADMIN
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, footerParams, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY (solo si hay email)
      if (footerParams.email) {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, footerParams, EMAILJS_PUBLIC_KEY);
      }
      setFooterStatus('sent');
      setFooterName('');
      setFooterPhone('');
      setFooterBusiness('');
      setTimeout(() => setFooterStatus('idle'), 3000);
    } catch (error) {
      setFooterStatus('error');
      setTimeout(() => setFooterStatus('idle'), 3000);
    }
  };
  const services = ['Meta Ads · Google Ads · TikTok Ads · YouTube Ads', 'Email · SMS · Automated Retargeting', 'AI Automation for 24/7 Sales', 'Surgical Funnels by Industry', 'Digital Shielding & Anti-Ban', 'Digital PR & Media Positioning', 'CRM & Advanced Data Tracking', 'AI Video Marketing · SEO · Branding', 'Reputation Management · Google Maps', 'Web Design · Landing Pages · CRO'];
  const resources = [{
    label: 'Strategy Blog',
    content: 'Coming soon we will publish articles, guides, and analysis on client acquisition strategies for local businesses in the USA. From how to structure your offer to how to scale your advertising campaigns without wasting budget. Subscribe to our free diagnosis to be the first to receive exclusive content.'
  }, {
    label: 'Success Stories',
    content: 'We have helped more than 1,877 local businesses in the United States get clients predictably. From roofing in Houston to beauty salons in Los Angeles, our A.C.E.™ System has generated measurable results: more calls, more appointments, and more sales. Visit the results section on this page to see real testimonials from our clients.'
  }, {
    label: 'Privacy Policy',
    content: 'At Doctor Ecommerce we respect your privacy. The information you share with us (name, phone, business type, etc.) is used exclusively to contact you and offer you our free diagnosis. We do not sell, share, or distribute your personal data to third parties under any circumstances. We use industry-standard encryption to protect your information. By submitting the form, you agree that we may contact you by phone, SMS, or email to schedule your diagnosis. You can request the deletion of your data at any time by writing to doctor.ecommerceus@gmail.com.'
  }, {
    label: 'Terms and Conditions',
    content: 'By using the services of Doctor Ecommerce, you accept the following terms: (1) The free diagnosis is an initial consultation without purchase commitment. (2) The results shown in testimonials are real but may vary depending on area, industry, offer, and business response capacity. (3) The 30-day A.C.E.™ Guarantee applies under the specific conditions detailed in the guarantee section of this page. (4) Prices and services are subject to change without notice. (5) Doctor Ecommerce reserves the right to reject clients who do not meet the qualification criteria. For legal inquiries, contact: doctor.ecommerceus@gmail.com | 402-872-0639 | 1001 Water St, Tampa FL 33602.'
  }];
  const certifications = [{
    src: 'https://cdn.magicpatterns.com/uploads/pBaETD69YuAZnEC2NMgyN6/IMG_0182.png',
    alt: 'Meta Business Partner'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/kjvZrE6BHjQdsLCQLEFT5k/IMG_0183.png',
    alt: 'Google Ads'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/tkQdMzRents4n11tVbsxCD/IMG_0184.jpg',
    alt: 'B2B Ecommerce Association'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/rk6HtZxNjdUiHRLfvC6PgD/IMG_0185.jpg',
    alt: 'Hortonworks Certified'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oNPxbVhsgjZQZJeXEMQQkL/IMG_0181.webp',
    alt: 'Shopify Experts'
  }];
  return <footer className="bg-[#070707]" data-id="element-1572">
      <div className="h-[2px] gold-gradient" data-id="element-1573" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8" data-id="element-1574">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-14" data-id="element-1575">
          <div className="lg:col-span-4" data-id="element-1576">
            <img src="https://cdn.magicpatterns.com/uploads/6TdzGPbST4y3Gk6CzyrcQT/IMG_6635.jpg" alt="Doctor Ecommerce Logo" className="h-14 mb-5" data-id="element-1577" />
            <h3 className="gold-text font-bold text-xl mb-4" data-id="element-1578">
              The cure for stagnant businesses.
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-1579">
              At Doctor Ecommerce we don't manage social media, we operate
              high-performance acquisition systems. Strategically headquartered
              in Tampa, Florida, we serve business owners across the United
              States, transforming advertising budgets into daily call and sales
              generation machines.
            </p>
            <div className="flex items-center gap-5" data-id="element-1580">
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="Instagram" data-id="element-1581">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-1582">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-1583" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="Facebook" data-id="element-1584">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-1585">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-1586" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="LinkedIn" data-id="element-1587">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-1588">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-1589" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="TikTok" data-id="element-1590">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-1591">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" data-id="element-1592" />
                </svg>
              </a>
            </div>
          </div>

          <div className="lg:col-span-3" data-id="element-1593">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-1594">
              Services
            </h3>
            <ul className="space-y-3" data-id="element-1595">
              {services.map((service, index) => <li key={index} className="text-gray-400 text-sm leading-relaxed" data-id="element-1596">
                  {service}
                </li>)}
            </ul>
          </div>

          <div className="lg:col-span-2" data-id="element-1597">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-1598">
              Resources
            </h3>
            <ul className="space-y-3" data-id="element-1599">
              {resources.map((resource, index) => <li key={index} data-id="element-1600">
                  <button onClick={() => setOpenResource(openResource === index ? null : index)} className="text-gray-400 text-sm hover:text-[#D4A843] transition-colors text-left cursor-pointer flex items-center gap-1.5 w-full" data-id="element-1601">
                    <ChevronDownIcon className={`w-3 h-3 flex-shrink-0 transition-transform duration-300 ${openResource === index ? 'rotate-180 text-[#D4A843]' : ''}`} data-id="element-1602" />
                    <span className={openResource === index ? 'text-[#D4A843]' : ''} data-id="element-1603">
                      {resource.label}
                    </span>
                  </button>
                  {openResource === index && <div className="mt-2 ml-4.5 pl-3 border-l border-[#D4A843]/20 animate-[fadeIn_0.3s_ease-out]" data-id="element-1604">
                      <p className="text-gray-500 text-xs leading-relaxed" data-id="element-1605">
                        {resource.content}
                      </p>
                    </div>}
                </li>)}
            </ul>
          </div>

          <div className="lg:col-span-3" data-id="element-1606">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-1607">
              Direct Contact
            </h3>
            <div className="space-y-3 mb-5" data-id="element-1608">
              <div className="flex items-start gap-3" data-id="element-1609">
                <MapPinIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0 mt-0.5" data-id="element-1610" />
                <span className="text-gray-400 text-sm" data-id="element-1611">
                  1001 WATER ST, TAMPA FL 33602
                </span>
              </div>
              <a href="tel:+14028720639" className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#D4A843] transition-colors" data-id="element-1612">
                <PhoneIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-1613" />
                <span data-id="element-1614">402-872-0639</span>
              </a>
              <a href="mailto:doctor.ecommerceus@gmail.com" className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#D4A843] transition-colors" data-id="element-1615">
                <MailIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-1616" />
                <span data-id="element-1617">doctor.ecommerceus@gmail.com</span>
              </a>
            </div>

            <div className="flex flex-col gap-2.5" data-id="element-1618">
              <a href="https://wa.me/message/TVKVR7FDMKG3P1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors" data-id="element-1619">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-1620">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-1621" />
                </svg>
                <span data-id="element-1622">Talk to a Specialist</span>
              </a>
              <a href="#formulario" onClick={e => {
              e.preventDefault();
              const formSection = document.getElementById('formulario');
              if (formSection) {
                formSection.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }} className="flex items-center justify-center gap-2 w-full gold-gradient text-black font-bold py-2.5 px-4 rounded-lg text-sm transition-all hover:opacity-90 cursor-pointer" data-id="element-1623">
                <CalendarIcon className="w-4 h-4" data-id="element-1624" />
                <span data-id="element-1625">BOOK DIAGNOSIS</span>
              </a>
            </div>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-10" data-id="element-1626" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10" data-id="element-1627">
          <div data-id="element-1628">
            <p className="text-gray-500 text-xs mb-5 uppercase tracking-widest" data-id="element-1629">
              Certified Partners and Elite Technology
            </p>
            <div className="flex flex-wrap items-center gap-3" data-id="element-1630">
              {certifications.map((cert, index) => <div key={index} className="bg-white rounded-lg p-1.5 h-10 md:h-12 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300" data-id="element-1631">
                  <img src={cert.src} alt={cert.alt} className="h-full w-auto object-contain max-w-[80px] md:max-w-[100px]" data-id="element-1632" />
                </div>)}
            </div>
          </div>

          <div data-id="element-1633">
            <form onSubmit={handleFooterSubmit} data-id="element-1634">
              <p className="text-gray-500 text-xs mb-5 uppercase tracking-widest" data-id="element-1635">
                Want us to contact you?
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5" data-id="element-1636">
                <input type="text" placeholder="Your Name" value={footerName} onChange={e => setFooterName(e.target.value)} required className="flex-1 px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-1637" />
                <input type="tel" placeholder="Your Phone" value={footerPhone} onChange={e => setFooterPhone(e.target.value)} required className="flex-1 px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-1638" />
                <input type="text" placeholder="Business Name" value={footerBusiness} onChange={e => setFooterBusiness(e.target.value)} required className="flex-1 px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-1639" />
                <button type="submit" disabled={footerStatus === 'sending'} className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex-shrink-0 border border-transparent ${footerStatus === 'sent' ? 'bg-[#059669] text-white' : footerStatus === 'error' ? 'bg-red-500 text-white' : 'gold-gradient text-black emerald-glow-hover'}`} data-id="element-1640">
                  {footerStatus === 'sending' ? '...' : footerStatus === 'sent' ? '✓' : footerStatus === 'error' ? '✕' : 'SEND'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-6" data-id="element-1641" />
        <p className="text-gray-600 text-xs text-center" data-id="element-1642">
          © 2024 Doctor Ecommerce. All rights reserved. Built for High
          Performance.
        </p>
      </div>
    </footer>;
}
export function LandingPageEN() {
  // Meta Pixel: Track PageView on mount
  useEffect(() => {
    if (window.fbq) window.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-[#0A0A0A]" data-id="element-1643">
      <NavigationHeaderEN data-id="element-1644" />
      <HeroSection data-id="element-1645" />
      <ProblemSection data-id="element-1646" />
      <SystemSection data-id="element-1647" />
      <IdentitySection data-id="element-1648" />
      <ResultsSection data-id="element-1649" />
      <MediaSection data-id="element-1650" />
      <AntiGuruSection data-id="element-1651" />
      <ServicesSection data-id="element-1652" />
      <QualificationSection data-id="element-1653" />
      <GuaranteeSection data-id="element-1654" />
      <FAQSection data-id="element-1655" />
      <FormSection data-id="element-1656" />
      <Footer data-id="element-1657" />
    </div>;
}